'use client';
// Registre des risques et des opportunités — ISO 15189:2022 §8.5.
//
// La norme traite les deux dans la même clause, et l'écran les garde ensemble :
// séparer en deux registres reviendrait à ne jamais ouvrir celui des
// opportunités. Mais une opportunité ne se COTE pas — elle n'a ni gravité ni
// probabilité, seulement un traitement et une échéance.
//
// Deux choses que cet écran refuse de faire :
//
//  · Rendre un verdict sur une criticité. Tant que le laboratoire n'a pas fixé
//    ses seuils, un 48 sur 125 s'affiche comme un nombre, pas comme un feu
//    rouge. Le niveau de risque qu'on accepte de porter est une décision de
//    direction, pas un défaut de paramétrage.
//
//  · Traiter une cotation incomplète comme une cotation faible. Deux notes sur
//    trois, ce n'est pas « peu critique » : c'est « pas coté ».
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ligneOuvrable, SansOuvrir } from '@/shared/Tableau';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { Skeleton,
  I, card, inputStyle, EmptyState, FilterBar, RefISO,
  FieldError, RequiredHint, useBusy, Selecteur } from '@/components/ui';
import { H1 } from './ressources/commun';
import { useFormulaire } from '@/components/Formulaire';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
type Ligne = {
  documentId: string; ref: string; titre: string; description: string | null;
  opportunite: boolean; processus: string | null; origine: string | null; consequence: string | null;
  gravite: number | null; probabilite: number | null; detectabilite: number | null; criticite: number | null;
  graviteResiduelle: number | null; probabiliteResiduelle: number | null;
  detectabiliteResiduelle: number | null; criticiteResiduelle: number | null;
  maitriseExistante: string | null; strategie: string | null; traitement: string | null;
  responsable: string | null; echeance: string | null; statut: string;
  dateIdentification: string | null; identifiePar: string | null;
  dateRevue: string | null; prochaineRevue: string | null; efficacite: string | null;
  cotationIncomplete: boolean; revueEchue: boolean; enRetard: boolean;
};
type Compte = {
  total: number; risques: number; opportunites: number; ouverts: number;
  sansCotation: number; sansStrategie: number; revueEchue: number; enRetard: number;
};
type Dossier = {
  lignes: Ligne[];
  seuils: { tolerable?: number; majeur?: number } | null;
  bornes: { min: number; max: number; criticiteMax: number };
  compte: Compte;
};

const STRATEGIES: Record<string, string> = {
  eviter: 'Éviter', reduire: 'Réduire', transferer: 'Transférer', accepter: 'Accepter',
};
const STATUTS: Record<string, { label: string; bg: string; fg: string }> = {
  identifie: { label: 'Identifié', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  en_traitement: { label: 'En traitement', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  traite: { label: 'Traité', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  accepte: { label: 'Accepté', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  clos: { label: 'Clos', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};

/** Criticité affichée. Un nombre, et rien de plus tant qu'aucun seuil n'est
 *  fixé : colorer en rouge au-delà d'une valeur qu'on aurait choisie soi-même
 *  serait décider du risque acceptable à la place du laboratoire. */
function Criticite({ l, seuils, max }: { l: Ligne; seuils: Dossier['seuils']; max: number }) {
  if (l.opportunite) {
    return <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>opportunité</span>;
  }
  if (l.criticite === null) {
    return (
      <span title="Les trois notes ne sont pas toutes renseignées : ce risque n’est pas coté. Ce n’est pas la même chose qu’un risque faible."
        style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 700, cursor: 'help' }}>
        non coté
      </span>
    );
  }
  const fort = seuils?.majeur !== undefined && l.criticite >= seuils.majeur;
  const moyen = !fort && seuils?.tolerable !== undefined && l.criticite > seuils.tolerable;
  return (
    <span title={seuils ? undefined : 'Aucun seuil d’acceptabilité n’est fixé : cette valeur n’est ni tolérable ni intolérable, elle est seulement calculée.'}
      style={{
        fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700,
        color: fort ? 'var(--red-600)' : moyen ? 'var(--amber-600)' : 'var(--text-strong)',
        cursor: seuils ? 'default' : 'help',
      }}>
      {l.criticite}<span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> / {max}</span>
      {l.criticiteResiduelle !== null && (
        <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> → {l.criticiteResiduelle}</span>
      )}
    </span>
  );
}

/** Saisie d'un risque ou d'une opportunité. Les trois notes sont demandées
 *  ensemble : une cotation partielle ne produit pas de criticité. */
function Formulaire({ ligne, bornes, onFait, onAnnule }: {
  ligne: Ligne | null; bornes: Dossier['bornes']; onFait: () => void; onAnnule: () => void;
}) {
  const { settings } = useGed();
  const gedUsers = settings?.gedUsers || [];
  const [f, setF] = useState(() => ({
    titre: ligne?.titre || '', description: ligne?.description || '',
    opportunite: ligne?.opportunite || false,
    processus: ligne?.processus || '', origine: ligne?.origine || '',
    consequence: ligne?.consequence || '', maitriseExistante: ligne?.maitriseExistante || '',
    gravite: ligne?.gravite ?? '', probabilite: ligne?.probabilite ?? '', detectabilite: ligne?.detectabilite ?? '',
    strategie: ligne?.strategie || '', traitement: ligne?.traitement || '',
    responsable: ligne?.responsable || '', echeance: ligne?.echeance || '',
    prochaineRevue: ligne?.prochaineRevue || '', statut: ligne?.statut || 'identifie',
  }));
  /* ⚠️ CE QUE L'ÉCRAN A LU, FIGÉ À L'OUVERTURE (14/10/2026), joint sous `base`
     avec la forme exacte du corps : le moteur n'écrit que ce qui a changé
     depuis cette lecture. Sans cela, un formulaire resté ouvert pendant qu'un
     autre clôturait le risque le rouvrait en renvoyant l'ancien statut. */
  const [base] = useState<Record<string, unknown>>(() => {
    const lu: Record<string, unknown> = { ...f };
    for (const k of ['gravite', 'probabilite', 'detectabilite']) {
      lu[k] = lu[k] === '' ? null : Number(lu[k]);
    }
    return lu;
  });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const notes = [f.gravite, f.probabilite, f.detectabilite];
  const cotationPartielle = !f.opportunite
    && notes.some((n) => n !== '') && notes.some((n) => n === '');
  const apercu = notes.every((n) => n !== '')
    ? Number(f.gravite) * Number(f.probabilite) * Number(f.detectabilite) : null;

  /* `cotationPartielle` n'est pas un champ manquant mais une valeur ERRONÉE :
     les trois notes existent peut-être, c'est leur combinaison qui ne produit
     pas de criticité. Le socle distingue les deux, et le bandeau le dit. */
  const declaration = React.useMemo(() => [
    { id: 'r-titre', label: 'Intitulé', valeur: f.titre, requis: true },
    {
      id: 'r-gravite', label: 'Cotation (gravité, probabilité, détectabilité)', valeur: f.gravite,
      erreur: cotationPartielle ? 'renseignez les trois notes, ou aucune : une cotation partielle ne produit pas de criticité' : null,
    },
  ], [f.titre, f.gravite, cotationPartielle]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const enregistrer = () => run('save', async () => {
    if (!verifier()) return;
    setErreur('');
    const corps: Record<string, unknown> = { ...f };
    for (const k of ['gravite', 'probabilite', 'detectabilite']) {
      corps[k] = f[k as 'gravite'] === '' ? null : Number(f[k as 'gravite']);
    }
    if (ligne) corps.base = base;
    const r = await fetchEcriture(`/api/ged/ged/risques${ligne ? `/${ligne.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Enregistrement refusé.');
      return;
    }
    onFait();
  });

  const champNote = (cle: 'gravite' | 'probabilite' | 'detectabilite', titre: string, aide: string) => (
    <div>
      <label style={lbl} htmlFor={`r-${cle}`} title={aide}>{titre}</label>
      <select id={`r-${cle}`} value={String(f[cle])} onChange={(e) => maj(cle, e.target.value)}
        style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
        <option value="">— non coté —</option>
        {Array.from({ length: bornes.max - bornes.min + 1 }, (_, i) => bornes.min + i).map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <Bandeau />
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {ligne ? `Modifier ${ligne.ref}` : 'Inscrire au registre'}
        </h2>
        <RequiredHint />
      </div>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
        <input type="checkbox" checked={f.opportunite} onChange={(e) => maj('opportunite', e.target.checked)}
          style={{ accentColor: 'var(--color-primary)' }} />
        C’est une <strong>opportunité</strong>, pas un risque
      </label>
      {f.opportunite && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: -6 }}>
          Une opportunité ne se cote pas : elle se suit par son traitement et son échéance.
        </div>
      )}

      <div>
        <label style={lbl} htmlFor="r-titre">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
        <input id="r-titre" value={f.titre} onChange={(e) => maj('titre', e.target.value)}
          placeholder={f.opportunite ? 'Ex. : mutualiser les CQI avec le laboratoire partenaire' : 'Ex. : rupture de la chaîne du froid pendant le transport'}
          style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor="r-proc">Processus concerné</label>
          <input id="r-proc" value={f.processus} onChange={(e) => maj('processus', e.target.value)}
            placeholder="Pré-analytique, analytique…" style={inputStyle} />
        </div>
        <div>
          <label style={lbl} htmlFor="r-orig">Origine</label>
          <input id="r-orig" value={f.origine} onChange={(e) => maj('origine', e.target.value)}
            placeholder="Audit, NC, réclamation, veille…" style={inputStyle} />
        </div>
      </div>

      <div>
        <label style={lbl} htmlFor="r-cons">Conséquence redoutée</label>
        <textarea id="r-cons" value={f.consequence} onChange={(e) => maj('consequence', e.target.value)}
          rows={2} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>
      <div>
        <label style={lbl} htmlFor="r-mait">Maîtrise déjà en place</label>
        <textarea id="r-mait" value={f.maitriseExistante} onChange={(e) => maj('maitriseExistante', e.target.value)}
          rows={2} placeholder="Ce qui limite DÉJÀ ce risque — la cotation en tient compte."
          style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      {!f.opportunite && (
        <div>
          <div style={lbl}>Cotation — les trois notes vont ensemble</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, alignItems: 'end' }}>
            {champNote('gravite', 'Gravité', 'Ampleur du dommage si le risque se réalise.')}
            {champNote('probabilite', 'Probabilité', 'Fréquence attendue de survenue.')}
            {champNote('detectabilite', 'Détectabilité', 'Note FORTE = on le détecte MAL. C’est la convention AMDEC : plus c’est difficile à voir venir, plus la criticité monte.')}
            <div style={{ paddingBottom: 8 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Criticité : </span>
              <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-strong)' }}>
                {apercu === null ? '—' : `${apercu} / ${bornes.criticiteMax}`}
              </strong>
            </div>
          </div>
          {cotationPartielle && (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 5 }}>
              Cotation partielle : renseignez les trois notes, ou laissez-les toutes vides.
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
        {!f.opportunite && (
          <div>
            <label style={lbl} htmlFor="r-strat">Stratégie</label>
            <select id="r-strat" value={f.strategie} onChange={(e) => maj('strategie', e.target.value)}
              style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              <option value="">— à décider —</option>
              {Object.entries(STRATEGIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={lbl} htmlFor="r-resp">Responsable</label>
          <Selecteur id="r-resp" value={f.responsable} onChange={(v) => maj('responsable', v)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
            <option value="">—</option>
            {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </Selecteur>
        </div>
        <div>
          <label style={lbl} htmlFor="r-ech">Échéance</label>
          <input id="r-ech" type="date" value={f.echeance} onChange={(e) => maj('echeance', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="r-rev">Prochaine revue</label>
          <input id="r-rev" type="date" value={f.prochaineRevue} onChange={(e) => maj('prochaineRevue', e.target.value)}
            style={{ ...inputStyle, height: 34, fontSize: 12.5 }} />
        </div>
        {ligne && (
          <div>
            <label style={lbl} htmlFor="r-stat">Statut</label>
            <select id="r-stat" value={f.statut} onChange={(e) => maj('statut', e.target.value)}
              style={{ ...inputStyle, height: 34, fontSize: 12.5 }}>
              {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        )}
      </div>

      <div>
        <label style={lbl} htmlFor="r-trait">Traitement retenu</label>
        <textarea id="r-trait" value={f.traitement} onChange={(e) => maj('traitement', e.target.value)}
          rows={2} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
      </div>

      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

export function Risques() {
  const { session, settings } = useGed();
  const isRQ = session.roles.includes('rq');
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [edite, setEdite] = useState<Ligne | null>(null);
  const [saisie, setSaisie] = useState(false);
  const { run, estOccupe } = useBusy();

  // `.then()` et non `await` dans l'effet : une fonction `async` appelée
  // directement masque le fait que les `setState` suivent le premier await.
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/risques', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => { setSaisie(false); setEdite(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || []).filter((l) => (
      filtre === 'risques' ? !l.opportunite
        : filtre === 'opportunites' ? l.opportunite
          : filtre === 'noncotes' ? l.cotationIncomplete
            : filtre === 'sansstrategie' ? !l.opportunite && !String(l.strategie || '').trim()
              : filtre === 'echus' ? l.revueEchue || l.enRetard
                : true));
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((l) => `${l.ref} ${l.titre} ${l.processus} ${l.origine} ${l.consequence}`.toLowerCase().includes(qq))
      : base;
  }, [d, filtre, q]);

  /* `Skeleton` et non une phrase grise (24/08/2026) : l'attente se dit de la
     même façon sur les 32 autres écrans, et le gabarit annonce la FORME de ce
     qui arrive au lieu de laisser la page vide. */
  if (chargement) return <Skeleton rows={5} />;
  if (!d) return null;
  const { compte, seuils, bornes } = d;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Amélioration · <RefISO code="8.5" /></>} title="Risques & opportunités" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Les deux vivent dans le même registre, parce que la norme les traite ensemble et qu’un
        registre d’opportunités séparé ne s’ouvre jamais. Mais une opportunité <strong>ne se
        cote pas</strong> : elle se suit par son traitement et son échéance.
      </p>

      {!seuils && compte.risques > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Aucun seuil d’acceptabilité n’est fixé.</strong>{' '}
          Les criticités sont calculées et classées, mais aucun risque n’est déclaré tolérable
          ni intolérable — le niveau de risque qu’on accepte de porter est une décision de
          direction, pas un réglage de l’outil.
        </div>
      )}
      {compte.sansCotation > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong>{compte.sansCotation} risque{compte.sansCotation > 1 ? 's' : ''} sans cotation complète.</strong>{' '}
          Deux notes sur trois ne font pas un risque faible : elles font un risque non coté.
        </div>
      )}

      {compte.total === 0 && !saisie && (
        <EmptyState
          titre="Le registre est vide."
          indice="Aucun risque ni opportunité n’a encore été inscrit. Ce n’est pas « aucun risque » : c’est que rien n’a encore été recensé — et c’est exactement ce que la revue de direction affichera."
          action={isRQ ? (
            <Bouton variante="primaire" onClick={() => setSaisie(true)}>
              <I n="plus" s={14} />Inscrire le premier
            </Bouton>
          ) : undefined} />
      )}

      {(saisie || edite) && isRQ && (
        <Formulaire ligne={edite} bornes={bornes} onFait={recharger}
          onAnnule={() => { setSaisie(false); setEdite(null); }} />
      )}

      {compte.total > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un risque, un processus…"
                chips={[
                  { k: 'tous', label: 'Tous', n: compte.total },
                  { k: 'risques', label: 'Risques', n: compte.risques },
                  { k: 'opportunites', label: 'Opportunités', n: compte.opportunites },
                  { k: 'noncotes', label: 'Non cotés', n: compte.sansCotation },
                  { k: 'sansstrategie', label: 'Sans stratégie', n: compte.sansStrategie },
                  { k: 'echus', label: 'Échéance ou revue dépassée', n: compte.enRetard + compte.revueEchue },
                ]}
                actif={filtre} onChip={setFiltre}
                total={compte.total} affiches={filtrees.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
            </div>
            {isRQ && !saisie && !edite && (
              <Bouton variante="primaire" taille="sm" onClick={() => setSaisie(true)}>
                <I n="plus" s={13} />Inscrire
              </Bouton>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((l, i) => {
              const st = STATUTS[l.statut] || STATUTS.identifie;
              return (
                /* Le clic sur la ligne ouvre le risque : « Modifier » était
                   répété sur chaque ligne du registre pour le geste qu'on
                   attend d'un clic (Ines, 26/08/2026). Les actions qui restent
                   sont enveloppées — leur clic ne doit pas ouvrir la fiche
                   par-dessus. */
                <div key={l.documentId}
                  {...(isRQ
                    ? ligneOuvrable(() => { setSaisie(false); setEdite(l); }, `Modifier ${l.ref}`,
                      { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' })
                    : { style: { padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' } })}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    {/* La traçabilité au même endroit que partout : l'icône du
                        journal suit la référence (règle RQ du 18/08, câblée le
                        24/08/2026 — ce registre écrivait au journal sans le
                        montrer). */}
                    <SansOuvrir><BoutonJournal reference={l.ref} quoi="de ce risque" /></SansOuvrir>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.titre}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: st.bg, color: st.fg }}>{st.label}</span>
                    {l.strategie && (
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{STRATEGIES[l.strategie] || l.strategie}</span>
                    )}
                    <span style={{ flex: 1 }} />
                    <Criticite l={l} seuils={seuils} max={bornes.criticiteMax} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {l.processus && <span>{l.processus}</span>}
                    {l.responsable && <span>{userName(settings, l.responsable)}</span>}
                    {l.echeance && (
                      <span style={{ color: l.enRetard ? 'var(--red-600)' : undefined, fontWeight: l.enRetard ? 700 : 400 }}>
                        échéance {fmtDate(l.echeance)}{l.enRetard ? ' — dépassée' : ''}
                      </span>
                    )}
                    {l.prochaineRevue && (
                      <span style={{ color: l.revueEchue ? 'var(--red-600)' : undefined, fontWeight: l.revueEchue ? 700 : 400 }}>
                        revue {fmtDate(l.prochaineRevue)}{l.revueEchue ? ' — échue' : ''}
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    {isRQ && (
                      <SansOuvrir>
                        <Bouton taille="sm" onClick={() => void run(l.documentId, async () => {
                          const r = await fetchEcriture(`/api/ged/ged/risques/${l.documentId}/action`, {
                            method: 'POST', credentials: 'include',
                            headers: { 'Content-Type': 'application/json' }, body: '{}',
                          });
                          if (r.ok) recharger();
                        })} disabled={estOccupe()} title="Porter le traitement au plan d’actions : c’est ce qui lui donne un responsable suivi et une échéance relancée.">
                          → plan d’actions
                        </Bouton>
                      </SansOuvrir>
                    )}
                  </div>
                  {l.consequence && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4 }}>{l.consequence}</div>
                  )}
                </div>
              );
            })}
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucune ligne ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
